/**
 * Toast notifications: short, non-blocking confirmations of what just
 * happened, top right under the header. Success toasts dismiss themselves
 * after 3 s, errors after 6 s (they carry the reason); either can be closed.
 * A bar along the bottom counts the time down: it starts full and empties,
 * and its animation ending is what dismisses the toast, so the two never
 * drift. Hovering or focusing a toast pauses both; leaving resumes them.
 *
 * `toast` is a plain module store, so code outside React (the query client's
 * MutationCache, see main.tsx) can raise one. Every save declares its message
 * on its mutation (`meta.success` in api/queries/) and failures toast on
 * their own; screens rarely call `toast` directly.
 */

import { useState, useSyncExternalStore, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';

type Kind = 'success' | 'error';

interface Item {
  id: number;
  kind: Kind;
  title: string;
  detail?: string;
}

const LIFETIME_MS: Record<Kind, number> = { success: 3000, error: 6000 };
const MAX_SHOWN = 4;

let items: Item[] = [];
let nextId = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function push(kind: Kind, title: string, detail?: string) {
  items = [...items, { id: (nextId += 1), kind, title, detail }].slice(-MAX_SHOWN);
  emit();
}

function dismiss(id: number) {
  items = items.filter((item) => item.id !== id);
  emit();
}

export const toast = {
  success: (title: string, detail?: string) => push('success', title, detail),
  error: (title: string, detail?: string) => push('error', title, detail),
  /** Removes every toast at once (the test harness starts each test with none). */
  clear: () => {
    items = [];
    emit();
  },
};

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function ToastCard({ item }: { item: Item }) {
  const [paused, setPaused] = useState(false);

  const Icon = item.kind === 'success' ? CheckCircle2 : TriangleAlert;
  return (
    <div
      className={`toast toast-${item.kind}${paused ? ' is-paused' : ''}`}
      style={{ '--toast-lifetime': `${LIFETIME_MS[item.kind]}ms` } as CSSProperties}
      role={item.kind === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <Icon aria-hidden="true" className="toast-icon" />
      <div className="toast-body">
        <b>{item.title}</b>
        {item.detail && <span>{item.detail}</span>}
      </div>
      <button type="button" className="toast-close" aria-label={`Dismiss: ${item.title}`} onClick={() => dismiss(item.id)}>
        <X aria-hidden="true" />
      </button>
      <span className="toast-timer" aria-hidden="true" onAnimationEnd={() => dismiss(item.id)} />
    </div>
  );
}

/** Mounted once at the app root. */
export function Toaster() {
  const shown = useSyncExternalStore(subscribe, () => items);
  return createPortal(
    <div className="toaster" aria-live="polite">
      {shown.map((item) => (
        <ToastCard key={item.id} item={item} />
      ))}
    </div>,
    document.body,
  );
}
